import { z } from 'zod'
import { Mutator } from './base'
import { DataBase } from '@/lib/kysely'
import { useUserStore } from '@/stores/user'
import type { InsertQuery, UpdateQuery, DeleteQuery } from '@/lib/entity/base'

import { graphql } from '@/gql'
import type {
  Coassign_Regular_Holiday_Insert_Input,
  Coassign_Regular_Holiday_Set_Input
} from '@/gql/graphql'
import type { MutationOperationAndVariables } from '@/lib/mutationHelpers'
import { pick } from '@/lib/utils/functional'
import { type Reactive } from 'vue'

export const regularHolidaySchema = z.object({
  id: z.string().max(21),
  year: z.number(),
  day_of_week: z.string().max(10),
  day_off: z.boolean().default(false)
})

export type RegularHoliday = z.infer<typeof regularHolidaySchema>

export class RegularHolidayMutator extends Mutator<typeof regularHolidaySchema> {
  static readonly stageableClassName = 'RegularHolidayMutator'
  schema = regularHolidaySchema
  table = 'regular_holiday' as const

  static async load(id: string, year: number): Promise<Reactive<RegularHolidayMutator>> {
    const self = this.createInstance()
    // FIXME: need use watch query
    const base = await DataBase.selectFrom('regular_holiday')
      .selectAll()
      .where('id', '=', id)
      .executeTakeFirst()
    // sqliteの型から変更
    if (base) {
      self.base = { ...base, ...{ day_off: Boolean(base.day_off) } }
    }
    self.context = { id, year }
    return self
  }

  async updateLocalDB(): Promise<UpdateQuery[]> {
    const id = this.context.id!
    const rawData = { ...(await this.parsed)! }
    const params = pick(rawData, Object.keys(this.modified) as (keyof typeof this.modified)[])
    const sqliteParams = { ...params, ...{ day_off: Number(params.day_off) } }
    return [DataBase.updateTable(this.table).set(sqliteParams).where('id', '=', id).compile()]
  }

  async insertLocalDB(): Promise<InsertQuery[]> {
    const tenant_id = useUserStore().userInfo!.tenantId
    const params = { ...(await this.parsed)!, ...{ tenant_id } }
    const sqliteParams = { ...params, ...{ day_off: Number(params.day_off) } }
    return [DataBase.insertInto(this.table).values(sqliteParams).compile()]
  }

  async deleteLocalDB(): Promise<DeleteQuery[]> {
    throw Error('can not delete')
  }

  async asInsertMutation(commit_id: string): Promise<MutationOperationAndVariables[]> {
    const params = (await this.parsed)!
    const variables: Coassign_Regular_Holiday_Insert_Input = { ...params, ...{ commit_id } }
    return [{ mutation: insertRegularHolidayMutationDocument, variables: { input: variables } }]
  }

  async asDeleteMutation(): Promise<MutationOperationAndVariables[]> {
    throw Error('can not delete')
  }

  async asUpdateMutation(commit_id: string): Promise<MutationOperationAndVariables[]> {
    const id = this.context.id!
    const rawData = { ...(await this.parsed)! }
    const params = pick(rawData, Object.keys(this.modified) as (keyof typeof this.modified)[])
    const variables: Coassign_Regular_Holiday_Set_Input = { ...params, ...{ commit_id } }
    return [
      { mutation: updateRegularHolidayMutationDocument, variables: { id: id, input: variables } }
    ]
  }
}

const insertRegularHolidayMutationDocument = graphql(/* GraphQL */ `
  mutation insertRegularHoliday($input: coassign_regular_holiday_insert_input!) {
    insert_coassign_regular_holiday_one(object: $input) {
      created_at
    }
  }
`)

const updateRegularHolidayMutationDocument = graphql(/* GraphQL */ `
  mutation updateRegularHoliday($id: String!, $input: coassign_regular_holiday_set_input!) {
    update_coassign_regular_holiday_by_pk(pk_columns: { id: $id }, _set: $input) {
      updated_at
    }
  }
`)
